import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Building2, Plus } from 'lucide-react';
import { apartmentsQuery, type Apartment } from '@/lib/apartments';
import { usePageHeader } from '@/components/PageHeader';
import { ApiError } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';

export const Route = createFileRoute('/_app/apartments/')({
  loader: ({ context }) => context.queryClient.ensureQueryData(apartmentsQuery),
  component: ApartmentsPage,
});

function ApartmentsPage() {
  const { data, isLoading, error } = useQuery(apartmentsQuery);

  const is401 = error instanceof ApiError && error.status === 401;
  const errorMessage = is401
    ? 'Your session has expired.'
    : error instanceof ApiError
      ? error.message
      : error
        ? String(error)
        : null;

  // Title lives in the Topbar (navTitle); publish the live count as the subtitle
  // and the "New apartment" primary into the Topbar's actions slot.
  usePageHeader({
    subtitle: data
      ? `${data.length} unit${data.length === 1 ? '' : 's'} · ${data.filter((a) => a.is_active).length} active`
      : undefined,
    actions: (
      <Link to="/apartments/new" className={buttonVariants({ variant: 'primary', size: 'sm' })}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        New apartment
      </Link>
    ),
  });

  return (
    <>
      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {errorMessage && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
          <span>
            {errorMessage}
            {is401 && (
              <>
                {' '}
                <Link to="/login" className="font-semibold underline">
                  Sign in again
                </Link>
                .
              </>
            )}
          </span>
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card px-5 py-12 text-center">
          <Building2 className="mx-auto mb-3 h-8 w-8 text-muted" aria-hidden="true" />
          <p className="text-sm text-muted">No apartments yet.</p>
        </div>
      )}

      {data && data.length > 0 && (
        <section>
          <p className="mb-3 text-sm text-muted">
            {data.length} apartment{data.length !== 1 ? 's' : ''}
          </p>
          <ApartmentsTable apartments={data} />
        </section>
      )}
    </>
  );
}

export function ApartmentsTable({ apartments }: { apartments: Apartment[] }) {
  return (
    <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-sunken)] text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            <th scope="col" className="px-4 py-3">Code</th>
            <th scope="col" className="px-4 py-3">Title</th>
            <th scope="col" className="px-4 py-3 text-right">Default occ.</th>
            <th scope="col" className="px-4 py-3 text-right">Max occ.</th>
            <th scope="col" className="px-4 py-3">Setup</th>
            <th scope="col" className="px-4 py-3">Status</th>
            <th scope="col" className="px-4 py-3">Created</th>
          </tr>
        </thead>
        <tbody>
          {apartments.map((apt) => (
            <tr
              key={apt.id}
              className="border-b border-[var(--border-soft)] transition last:border-b-0 hover:bg-[var(--surface-sunken)]"
            >
              <td className="px-4 py-3 font-semibold">
                <Link
                  to="/apartments/$id"
                  params={{ id: apt.id }}
                  className="text-primary hover:underline"
                >
                  {apt.code}
                </Link>
              </td>
              <td className="px-4 py-3 text-[var(--text-heading)]">{apt.title}</td>
              <td className="px-4 py-3 text-right tabular-nums">{apt.occupancy_default}</td>
              <td className="px-4 py-3 text-right tabular-nums">{apt.occupancy_max}</td>
              <td className="px-4 py-3">
                {apt.setup_complete ? null : (
                  <Badge
                    variant="neutral"
                    title="Provisioning in progress; it finishes automatically."
                  >
                    Setting up
                  </Badge>
                )}
              </td>
              <td className="px-4 py-3">
                {apt.is_active ? (
                  <Badge variant="success" dot>
                    Active
                  </Badge>
                ) : (
                  <Badge variant="neutral" dot>
                    Inactive
                  </Badge>
                )}
              </td>
              <td className="px-4 py-3 text-xs text-muted">
                {new Date(apt.created_at).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
